/**
 * VesselFinder credit catalogue — frontend half of the shared vocabulary.
 *
 * Mirrors `eetaskapi/helpers/vesselFinderCredits.js` exactly — a drift spec
 * asserts the two agree (`vesselfinder-credit-actions.spec.ts`). The operator
 * document (`apiCreditActions` on the sensor row) is the source of truth; the
 * constants below are the last-resort fallback for a row that predates the
 * field.
 *
 * Five entries, one per billable REQUEST SHAPE (not per upstream endpoint —
 * `search`/`searchSatellite`/`master`/`voyage` all call `GET /vessels`, see
 * the backend helper's header). No bundled "lookup" (design decision
 * 2026-08-25): Master and Voyage are independent actions, independently
 * priced and independently controlled.
 */

export interface VesselFinderCreditAction {
  key: string;
  label: string;
  credits: number;
  description?: string;
}

export const VESSEL_FINDER_CREDIT_ACTIONS: VesselFinderCreditAction[] = [
  {
    key: 'search',
    label: 'Worldwide vessel search (terrestrial)',
    credits: 1,
    description: 'Find a vessel anywhere by MMSI or IMO via terrestrial AIS.',
  },
  {
    key: 'searchSatellite',
    label: 'Worldwide vessel search (satellite)',
    credits: 10,
    description: 'Find a vessel anywhere by MMSI or IMO via satellite AIS.',
  },
  {
    key: 'master',
    label: 'Master data',
    credits: 3,
    description: 'Vessel master data — flag, built, builder, owner, manager, class, tonnage, dimensions.',
  },
  {
    key: 'voyage',
    label: 'Voyage data',
    credits: 2,
    description: "Vessel's current voyage details — last port, departure, destination.",
  },
  {
    key: 'expectedArrivals',
    label: 'Expected port arrivals',
    credits: 5,
    description: 'Vessels expected at a port within the requested time window, using AIS destination data.',
  },
  {
    key: 'portCalls',
    label: 'Port call history',
    credits: 2,
    description: "One vessel's recorded arrivals, departures and time at anchorage.",
  },
];

/** Which catalogue key prices a search, based on the user's satellite toggle — mirrors the backend helper. */
export function searchActionKey(useSatellite: boolean): string {
  return useSatellite ? 'searchSatellite' : 'search';
}

/** Last-resort price for a key the doc does not carry (or carries invalidly). */
export function vesselFinderFallbackCredits(key: string): number {
  return VESSEL_FINDER_CREDIT_ACTIONS.find((a) => a.key === key)?.credits ?? 0;
}

/**
 * A sensor row's own catalogue, filled out with defaults for any key it is
 * missing — same "doc wins per key" rule as the backend helper's
 * `getVesselFinderCreditActions`. Mirrors `normalizeCreditActions` in
 * voyageradar-credit-actions.ts — used directly by the purchase dialog, and
 * by `actionCredits` below for the same per-key fallback.
 */
export function normalizeCreditActions(raw: any): VesselFinderCreditAction[] {
  const fromDoc = Array.isArray(raw)
    ? raw.filter(
        (a) => a && typeof a.key === 'string' && a.key.trim() !== '' && Number.isInteger(a.credits) && a.credits > 0,
      )
    : [];
  if (!fromDoc.length) return VESSEL_FINDER_CREDIT_ACTIONS;
  const docKeys = new Set(fromDoc.map((a) => a.key));
  const missing = VESSEL_FINDER_CREDIT_ACTIONS.filter((a) => !docKeys.has(a.key));
  return missing.length ? [...fromDoc, ...missing] : fromDoc;
}

/**
 * No target/AOI-POI concept for this operator in this phase (frontend plan
 * Q7) — every action applies regardless of tasked shape, so this is a
 * pass-through. Kept as its own function (rather than inlined at the one call
 * site) so the purchase dialog can be written against the same two-function
 * shape voyageradar-credit-actions.ts exposes, with nothing VesselFinder-
 * specific leaking into that shared file.
 */
export function actionsForTarget(actions: VesselFinderCreditAction[], _isPoi?: boolean): VesselFinderCreditAction[] {
  return actions;
}

/** Cheapest action in the catalogue — below this a wallet is stranded. */
export function minActionCredits(actions: VesselFinderCreditAction[]): number {
  const costs = (actions?.length ? actions : VESSEL_FINDER_CREDIT_ACTIONS).map((a) => a.credits);
  return Math.min(...costs);
}

/** Purchase bounds, as read off the operator doc — same null-max/derived-floor contract as Voyage Radar's `VoyageCreditBounds`. */
export interface VesselFinderCreditBounds {
  min: number;
  max: number | null;
}

/**
 * Purchase bounds off a sensor row / operator doc — mirrors
 * `resolveCreditBounds` in voyageradar-credit-actions.ts exactly (same
 * whole-number/inverted-pair/derived-floor rules); read that file's header
 * for the reasoning this one does not repeat.
 */
export function resolveCreditBounds(row: any, actions?: VesselFinderCreditAction[]): VesselFinderCreditBounds {
  const whole = (value: any): number | null => {
    const n = Number(value);
    return Number.isInteger(n) && n > 0 ? n : null;
  };

  let min = whole(row?.minApiCredits);
  let max = whole(row?.maxApiCredits);

  if (min !== null && max !== null && min > max) {
    min = null;
    max = null;
  }

  const cheapest = minActionCredits(actions ?? VESSEL_FINDER_CREDIT_ACTIONS);
  if (min === null || min < cheapest) min = cheapest;
  if (max !== null && max < min) min = max;

  return { min, max };
}

/** Credit cost of one action, from a sensor row's own catalogue (doc wins), falling back to the shipped defaults. */
export function actionCredits(row: any, key: string): number {
  const found = normalizeCreditActions(row?.apiCreditActions).find((a) => a.key === key);
  return found?.credits ?? vesselFinderFallbackCredits(key);
}

/**
 * Balance for display, or null when unknown. Same null-vs-zero contract as
 * `voyageCreditsRemaining` — see `vf-credits.model.ts`.
 */
export function canAfford(row: any, cost: number): boolean {
  const raw = row?.apiCreditsRemaining == 0 ? 0 : row?.apiCreditsRemaining || row?.apiCredits;
  const n = Number(raw);
  return Number.isFinite(n) && n >= cost;
}
